import React, { useMemo, useState } from 'react';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  CountryCode
} from 'libphonenumber-js/mobile';
import examples from 'libphonenumber-js/mobile/examples';

// Telefone internacional: seletor de país (DDI) + número nacional com máscara do país.
// Devolve sempre E.164 (+5511912345678) e se é um celular válido no país escolhido. A metadata
// "mobile" recusa fixo e celular BR sem o 9, que não abrem no WhatsApp.
type Props = {
  lang: string;
  onChange: (e164: string, valid: boolean) => void;
  defaultCountry?: CountryCode;
  placeholder?: string;
  ariaLabel?: string;
  countryAriaLabel?: string;
  /** Número já salvo em E.164 (+351912345678): abre com o país e a parte nacional preenchidos. */
  initialValue?: string;
};

const FAVORITES: CountryCode[] = ['BR', 'PT', 'US', 'ES', 'GB', 'FR', 'IT', 'DE', 'IE', 'CA'];
const MAX_DIGITS = 15;

const flag = (cc: string) =>
  String.fromCodePoint(...cc.toUpperCase().split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

function regionNames(lang: string): (cc: string) => string {
  try {
    const DisplayNames = (Intl as any).DisplayNames;
    const dn = new DisplayNames([lang], { type: 'region' });
    return (cc) => dn.of(cc) || cc;
  } catch {
    return (cc) => cc;
  }
}

// Tenta no país selecionado; se não fechar, tenta como número internacional (quem digita
// "5511912345678" com o DDI junto, sem o "+").
function parse(digits: string, country: CountryCode) {
  const local = parsePhoneNumberFromString(digits, country);
  if (local?.isValid()) return local;
  const intl = digits.length > 8 ? parsePhoneNumberFromString(`+${digits}`) : undefined;
  return intl?.isValid() ? intl : local;
}

const PhoneInput: React.FC<Props> = ({ lang, onChange, defaultCountry = 'BR', placeholder, ariaLabel, countryAriaLabel, initialValue }) => {
  const initial = useMemo(() => (initialValue ? parsePhoneNumberFromString(initialValue) : undefined), [initialValue]);
  const [country, setCountry] = useState<CountryCode>(initial?.country || defaultCountry);
  const [text, setText] = useState(() =>
    initial?.country ? new AsYouType(initial.country).input(String(initial.nationalNumber)) : ''
  );

  const options = useMemo(() => {
    const name = regionNames(lang);
    const toOpt = (cc: CountryCode) => ({ cc, name: name(cc), label: `${flag(cc)} ${name(cc)} (+${getCountryCallingCode(cc)})` });
    const all = getCountries()
      .filter((cc) => !FAVORITES.includes(cc))
      .map(toOpt)
      .sort((a, b) => a.name.localeCompare(b.name, lang));
    return { favorites: FAVORITES.map(toOpt), all };
  }, [lang]);

  const emit = (digits: string, cc: CountryCode) => {
    const phone = digits ? parse(digits, cc) : undefined;
    onChange(phone?.number || (digits ? `+${getCountryCallingCode(cc)}${digits}` : ''), !!phone?.isValid());
  };

  const handleText = (raw: string) => {
    // Colou/autofill com "+DDI": detecta o país e passa a mostrar só a parte nacional.
    if (raw.trim().startsWith('+')) {
      const phone = parsePhoneNumberFromString(raw);
      if (phone?.country) {
        const national = String(phone.nationalNumber);
        setCountry(phone.country);
        setText(new AsYouType(phone.country).input(national));
        emit(national, phone.country);
        return;
      }
    }
    let digits = raw.replace(/\D/g, '').slice(0, MAX_DIGITS);
    // Backspace sobre um caractere da máscara ("(", ")", "-") não remove dígito; sem isto a
    // máscara recolocaria o mesmo caractere e o campo pareceria travado.
    const prevDigits = text.replace(/\D/g, '');
    if (raw.length < text.length && digits === prevDigits) digits = digits.slice(0, -1);
    setText(digits ? new AsYouType(country).input(digits) : '');
    emit(digits, country);
  };

  const handleCountry = (cc: CountryCode) => {
    const digits = text.replace(/\D/g, '');
    setCountry(cc);
    setText(digits ? new AsYouType(cc).input(digits) : '');
    emit(digits, cc);
  };

  return (
    <div className="flex w-full border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent">
      <div className="relative shrink-0 border-r border-gray-300">
        {/* O select nativo fica invisível por cima do rótulo compacto (bandeira + DDI);
            ao tocar, abre a lista completa do sistema com o nome dos países. */}
        <span className="flex items-center gap-1 h-full pl-3 pr-2 text-base text-gray-700 pointer-events-none">
          <span aria-hidden="true">{flag(country)}</span>
          <span>+{getCountryCallingCode(country)}</span>
          <svg className="w-3 h-3 text-gray-400" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </span>
        <select
          value={country}
          onChange={(e) => handleCountry(e.target.value as CountryCode)}
          aria-label={countryAriaLabel}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer text-base"
        >
          <optgroup label="★">
            {options.favorites.map((o) => <option key={o.cc} value={o.cc}>{o.label}</option>)}
          </optgroup>
          <optgroup label="—">
            {options.all.map((o) => <option key={o.cc} value={o.cc}>{o.label}</option>)}
          </optgroup>
        </select>
      </div>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={text}
        onChange={(e) => handleText(e.target.value)}
        placeholder={getExampleNumber(country, examples)?.formatNational() || placeholder}
        aria-label={ariaLabel}
        className="flex-1 min-w-0 px-4 py-3 text-base bg-transparent border-0 rounded-r-lg focus:outline-none focus:ring-0"
      />
    </div>
  );
};

export default PhoneInput;
